/**
 * Video Helpers
 * Utility functions for Wistia video loading and initialization
 */

import { VIDEO_CONFIG } from '../constants/videoConfig';

export const isRecoverableWistiaMediaError = error => {
  const stringified = (() => {
    try {
      return String(error || '');
    } catch (e) {
      return '';
    }
  })();
  const message =
    typeof error === 'string'
      ? error
      : error?.message ||
        error?.reason?.message ||
        error?.target?.error?.message ||
        error?.error?.message ||
        stringified;
  const name = error?.name || error?.reason?.name || error?.target?.error?.name || '';
  const code = error?.code || error?.reason?.code || error?.target?.error?.code || error?.error?.code;

  return (
    name === 'MediaError' ||
    code === 4 ||
    stringified.includes('MediaError') ||
    message.includes('MediaError') ||
    message.includes('DEMUXER_ERROR_COULD_NOT_PARSE') ||
    message.includes('PipelineStatus')
  );
};

/**
 * Load Wistia script dynamically
 * @param {boolean} isMounted - Flag to check if component is still mounted
 * @returns {Promise} Resolves when script is loaded
 */
export const loadWistiaScript = isMounted => {
  return new Promise((resolve, reject) => {
    if (!isMounted) return;
    if (window.customElements?.get('wistia-player')) return resolve();

    const existingScript = document.querySelector(`script[src="${VIDEO_CONFIG.PLAYER_SCRIPT_URL}"]`);
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', reject, { once: true });
      return;
    }

    const scriptTimeout = setTimeout(() => {
      reject(new Error('Script loading timeout'));
    }, VIDEO_CONFIG.SCRIPT_TIMEOUT_MS);

    const wistiaScript = document.createElement('script');
    wistiaScript.src = VIDEO_CONFIG.PLAYER_SCRIPT_URL;
    wistiaScript.async = true;
    wistiaScript.onload = () => {
      clearTimeout(scriptTimeout);
      if (isMounted) resolve();
    };
    wistiaScript.onerror = error => {
      clearTimeout(scriptTimeout);
      if (isMounted) reject(error);
    };

    document.head.appendChild(wistiaScript);
  });
};

/**
 * Initialize Wistia video player
 * @param {string} videoId - Wistia video ID
 * @param {boolean} isMounted - Flag to check if component is still mounted
 * @param {function} setIsVideoLoading - Callback to set loading state
 * @returns {Promise} Resolves with video instance
 */
export const initializeWistiaVideo = async (videoId, isMounted, setIsVideoLoading) => {
  if (!isMounted) return null;

  try {
    const videoReady = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        resolve(null);
      }, VIDEO_CONFIG.VIDEO_READY_TIMEOUT_MS);

      window.wistiaOptions = window.wistiaOptions || {};
      window.wistiaOptions[videoId] = {
        onReady: video => {
          clearTimeout(timeout);
          resolve(video);
        },
        onError: error => {
          clearTimeout(timeout);
          if (isRecoverableWistiaMediaError(error)) {
            resolve(null);
          } else {
            reject(error);
          }
        },
      };
    });

    const video = await videoReady;
    if (!isMounted || !video) return null;

    if (typeof video.mute === 'function') {
      video.mute();
    }

    if (typeof video.play === 'function') {
      try {
        await video.play();
      } catch (error) {
        if (!isRecoverableWistiaMediaError(error)) {
          console.warn('Video play failed:', error);
        }
      }
    }

    if (isMounted && setIsVideoLoading) setIsVideoLoading(false);
    return video;
  } catch (error) {
    if (!isRecoverableWistiaMediaError(error)) {
      console.error('Error initializing video:', error);
    }
    if (isMounted && setIsVideoLoading) setIsVideoLoading(false);
    return null;
  }
};

/**
 * Create video container element with overlay
 * @param {string} videoId - Wistia video ID
 * @returns {HTMLElement} Video container element
 */
export const createVideoContainer = videoId => {
  const videoContainer = document.createElement('div');
  videoContainer.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  `;

  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.34);
    z-index: 1;
  `;

  const videoDiv = document.createElement('div');
  const videoPlayer = document.createElement('wistia-player');
  videoPlayer.setAttribute('media-id', videoId);
  videoPlayer.setAttribute('aspect', '1.7777777777777777');
  videoPlayer.setAttribute('autoplay', 'muted');
  videoPlayer.setAttribute('muted', 'true');
  videoPlayer.setAttribute('loop', 'true');
  videoPlayer.setAttribute('controls', 'false');
  videoPlayer.setAttribute('playsinline', 'true');
  videoPlayer.setAttribute('end-video-behavior', 'loop');
  videoPlayer.setAttribute('fit-strategy', 'cover');
  videoPlayer.setAttribute('allow-unmute-overlay', 'false');
  videoPlayer.setAttribute('click-to-play', 'false');
  videoPlayer.setAttribute('play-button', 'false');
  videoDiv.style.cssText = `
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background-color: #000;
  `;
  videoPlayer.style.cssText = `
    width: 100%;
    height: 100%;
    display: block;
  `;
  videoDiv.appendChild(videoPlayer);

  videoContainer.appendChild(overlay);
  videoContainer.appendChild(videoDiv);

  return videoContainer;
};
